import { AsyncPipe, DatePipe, LowerCasePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, CUSTOM_ELEMENTS_SCHEMA, EventEmitter, inject, Input, Output } from '@angular/core';
import { Observable } from 'rxjs';
import { LinguineoConversationDetail, LinguineoFullMessage } from '../../interfaces';
import { ConversationReviewService } from '../../services';

import '@diekeure/dcr-components/dcr-avatar.js';
import '@diekeure/dcr-components/dcr-dialog.js';

interface CommentPart {
  text: string;
  isSuggestion: boolean;
}

interface AnnotatedSegment {
  text: string;
  feedbackComment?: string;
  feedbackCommentParts?: CommentPart[];
  feedbackType?: string;
}

interface ReviewMessage {
  isBot: boolean;
  authorLabel: string;
  authorFirstName: string;
  authorLastName: string;
  authorAvatar: string;
  text: string;
  segments: AnnotatedSegment[];
  fragmentUrl?: string;
  feedbackCount: number;
}

export interface DialogReviewStudent {
  firstName?: string;
  lastName?: string;
  name?: string;
  displayName?: string;
  avatar?: string;
}

@Component({
  standalone: true,
  imports: [AsyncPipe, DatePipe, LowerCasePipe],
  selector: 'campus-dialog-review',
  templateUrl: './dialog-review.component.html',
  styleUrls: ['./dialog-review.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class DialogReviewComponent {
  @Input({ required: true }) resultId!: number;
  @Input() student?: DialogReviewStudent;
  @Output() closed = new EventEmitter<void>();

  public open = true;
  public detail$!: Observable<LinguineoConversationDetail>;

  private reviewService = inject(ConversationReviewService);

  ngOnInit(): void {
    this.detail$ = this.reviewService.getConversationDetail$(this.resultId);
  }

  public onDialogClosed(): void {
    this.open = false;
    this.closed.emit();
  }

  public toReviewMessages(detail: LinguineoConversationDetail): ReviewMessage[] {
    const personaLabelById = new Map((detail.personas ?? []).map((p) => [p.dbId, p.label]));
    const userLabelById = new Map((detail.users ?? []).map((u) => [u.dbId, u.label]));

    return (detail.messages ?? []).map((message) => this.toReviewMessage(message, personaLabelById, userLabelById));
  }

  private toReviewMessage(
    message: LinguineoFullMessage,
    personaLabelById: Map<number, string>,
    userLabelById: Map<number, string>
  ): ReviewMessage {
    const isBot = message.personaId != null;
    const studentDisplay = this.student?.displayName || [this.student?.firstName, this.student?.lastName ?? this.student?.name].filter(Boolean).join(' ');
    const authorLabel = isBot
      ? personaLabelById.get(message.personaId) ?? 'Bot'
      : studentDisplay || userLabelById.get(message.userId) || 'Leerling';
    const text = message.partOfConversation?.text ?? '';

    return {
      isBot,
      authorLabel,
      authorFirstName: isBot ? authorLabel : this.student?.firstName ?? '',
      authorLastName: isBot ? '' : this.student?.lastName ?? this.student?.name ?? '',
      authorAvatar: (isBot ? undefined : this.student?.avatar) ?? '',
      text,
      segments: this.annotate(text, message.feedback ?? []),
      fragmentUrl: message.partOfConversation?.fragmentUrl?.trim() || undefined,
      feedbackCount: message.feedback?.length ?? 0,
    };
  }

  // Slice the message text into plain + annotated segments using each feedback's
  // start/end positions so the template can highlight covered parts inline.
  private annotate(text: string, feedback: LinguineoFullMessage['feedback']): AnnotatedSegment[] {
    if (!feedback?.length) return [{ text }];

    const ranges = feedback
      .filter((f) => Number.isFinite(f.startPosition) && Number.isFinite(f.endPosition))
      .sort((a, b) => a.startPosition - b.startPosition);

    if (!ranges.length) return [{ text }];

    const segments: AnnotatedSegment[] = [];
    let cursor = 0;
    for (const range of ranges) {
      if (range.startPosition > cursor) {
        segments.push({ text: text.slice(cursor, range.startPosition) });
      }
      segments.push({
        text: text.slice(range.startPosition, range.endPosition),
        feedbackComment: range.comment,
        feedbackCommentParts: this.parseCommentSuggestions(range.comment),
        feedbackType: range.feedbackType,
      });
      cursor = range.endPosition;
    }
    if (cursor < text.length) segments.push({ text: text.slice(cursor) });
    return segments;
  }

  // Linguineo embeds suggested corrections inline as `<suggestion>...</suggestion>`
  // inside the feedback comment. Split the comment into plain + suggestion parts so
  // the template can render the suggestions with their own style.
  private parseCommentSuggestions(comment: string | undefined): CommentPart[] {
    if (!comment) return [];
    const parts: CommentPart[] = [];
    const regex = /<suggestion>([\s\S]*?)<\/suggestion>/g;
    let lastIndex = 0;
    let match: RegExpExecArray | null;
    while ((match = regex.exec(comment))) {
      if (match.index > lastIndex) {
        parts.push({ text: comment.slice(lastIndex, match.index), isSuggestion: false });
      }
      parts.push({ text: match[1], isSuggestion: true });
      lastIndex = regex.lastIndex;
    }
    if (lastIndex < comment.length) {
      parts.push({ text: comment.slice(lastIndex), isSuggestion: false });
    }
    return parts;
  }
}
